// Lastslate editor commands.
//
// Each editing primitive (Tab cycle, Enter element transition,
// Cmd-1..6 direct assign) is a ProseMirror command. Together with
// prosemirror-history's `undo`/`redo`, this replaces the entire
// custom undo/redo + selection-aware keyboard layer the legacy
// renderer wrapped around contenteditable.

import { toggleMark } from 'prosemirror-commands';
import { TextSelection } from 'prosemirror-state';
import { schema } from './schema.js';
import { openPaletteCommand } from './element-palette.js';

// Source of truth: legacy app.js ELEMENT_TRANSITIONS. Keys and values
// are in storage type (kebab-case); converted to schema node names
// (underscore) at the call site via `nodeTypeFor`.
const ELEMENT_TRANSITIONS = {
  'scene-heading': { onEnter: 'action', onTab: 'character' },
  action: { onEnter: 'action', onTab: 'character' },
  character: { onEnter: 'dialogue', onTab: 'parenthetical' },
  parenthetical: { onEnter: 'dialogue', onTab: 'dialogue' },
  dialogue: { onEnter: 'action', onTab: 'character' },
  transition: { onEnter: 'scene-heading', onTab: 'scene-heading' },
  shot: { onEnter: 'action', onTab: 'character' },
};

// Cmd-1..6 quick-assign — new behavior on top of the legacy app.
// Writers can change a block's type without leaving home row.
export const ELEMENT_QUICK_ASSIGN = {
  1: 'scene-heading',
  2: 'action',
  3: 'character',
  4: 'dialogue',
  5: 'parenthetical',
  6: 'transition',
};

const STORAGE_TO_NODE = {
  'scene-heading': 'scene_heading',
  action: 'action',
  character: 'character',
  parenthetical: 'parenthetical',
  dialogue: 'dialogue',
  transition: 'transition',
  shot: 'shot',
  centered: 'centered',
};

const NODE_TO_STORAGE = Object.fromEntries(Object.entries(STORAGE_TO_NODE).map(([s, n]) => [n, s]));

function nodeTypeFor(storageType) {
  const nodeName = STORAGE_TO_NODE[storageType];
  return nodeName ? schema.nodes[nodeName] : null;
}

function currentBlock(state) {
  const $from = state.selection.$from;
  // The block immediately containing the cursor (depth 1 for our flat
  // doc: doc > block).
  if ($from.depth < 1) return null;
  return { node: $from.node(1), depth: 1, pos: $from.before(1) };
}

function currentStorageType(state) {
  const cur = currentBlock(state);
  if (!cur) return null;
  return NODE_TO_STORAGE[cur.node.type.name] || null;
}

// Change the type of every block fully or partially in the selection.
// Equivalent to prosemirror-commands' setBlockType, but builds the
// transaction directly so the auto `scrollIntoView` flag is never set.
// Tab / Cmd-1..6 should NOT yank the viewport — the block is already
// on screen, that's how the writer reached the keybinding.
//
// The attrs function preserves each block's `bid` (UUIDv7) so the
// identity survives a type change — Tab-cycling an action to a
// character is still the same block. Other attrs deliberately reset
// to the new type's defaults (e.g. a forced action doesn't carry its
// `forced: true` forward into the character).
export function assignType(storageType) {
  const nodeType = nodeTypeFor(storageType);
  if (!nodeType) return () => false;
  return function (state, dispatch) {
    const { from, to } = state.selection;
    const tr = state.tr.setBlockType(from, to, nodeType, (oldNode) => ({
      bid: oldNode.attrs && oldNode.attrs.bid,
    }));
    if (!tr.docChanged) return false;
    if (dispatch) dispatch(tr);
    return true;
  };
}

// Tab cycles forward through the ELEMENT_TRANSITIONS table. Shift-Tab
// cycles back: if the current type is the target of a forward Tab,
// step back to the source. Ambiguous reverses (multiple types Tab to
// `character`) resolve to `action` — the most common origin.
function reverseOnTab(storageType) {
  switch (storageType) {
    case 'character':
      return 'action';
    case 'parenthetical':
      return 'character';
    case 'dialogue':
      return 'parenthetical';
    case 'scene-heading':
      return 'transition';
    default:
      return null;
  }
}

// Inside an EMPTY block the Tab cycle is a tight 3-stop loop —
// `action → character → transition → action → …` — so a writer who Tabs
// out of a fresh Action can reach FADE OUT: / CUT TO: without typing
// the `>` force-prefix, and Tabbing once more loops back to Action
// instead of dropping into Parenthetical. Parenthetical stays reachable
// via Cmd-5 / autocomplete / the `(` input-rule. Once the block has
// text the regular ELEMENT_TRANSITIONS table takes over, so an
// already-typed character or transition still cycles to its usual stop.
const EMPTY_BLOCK_FORWARD = {
  character: 'transition',
  transition: 'action',
};
const EMPTY_BLOCK_REVERSE = {
  action: 'transition',
  transition: 'character',
};

function isCurrentBlockEmpty(state) {
  const $from = state.selection.$from;
  return $from.depth >= 1 && $from.parent.content.size === 0;
}

export function cycleType(forward) {
  return function (state, dispatch) {
    const curType = currentStorageType(state);
    if (!curType) return false;
    if (isCurrentBlockEmpty(state)) {
      const override = forward ? EMPTY_BLOCK_FORWARD[curType] : EMPTY_BLOCK_REVERSE[curType];
      if (override) return assignType(override)(state, dispatch);
    }
    const nextType = forward
      ? (ELEMENT_TRANSITIONS[curType] && ELEMENT_TRANSITIONS[curType].onTab) || null
      : reverseOnTab(curType);
    if (!nextType || nextType === curType) return false;
    return assignType(nextType)(state, dispatch);
  };
}

// Enter handling. Three cases:
//   1. Cursor at END of block → insert a new block AFTER of the
//      onEnter type (scene_heading → action, character → dialogue,
//      etc.). Caret moves into the new block.
//   2. Cursor at START of a non-empty block → insert an empty ACTION
//      block BEFORE. Caret stays in the original block. This is the
//      "make room above" / "push content down" gesture. We force the
//      new block to ACTION (never the same type as the current block)
//      so a chain of Enter-at-start doesn't create a long sticky
//      chain of scene_headings / characters / parentheticals — those
//      chains break pagination (back-walk pushes the head across
//      multiple pages, compounding per Enter).
//   3. Cursor MID-block → fall through to baseKeymap's splitBlock.
export function elementTransitionOnEnter(state, dispatch) {
  const sel = state.selection;
  if (!sel.empty) return false;
  const cur = currentBlock(state);
  if (!cur) return false;
  const curType = NODE_TO_STORAGE[cur.node.type.name];
  if (!curType) return false;

  const $from = sel.$from;
  const atStart = $from.parentOffset === 0;
  const atEnd = $from.parentOffset === $from.parent.content.size;
  const blockIsEmpty = $from.parent.content.size === 0;

  // Case 2: at the start of a non-empty block → insert action above.
  if (atStart && !blockIsEmpty) {
    if (dispatch) {
      const actionType = schema.nodes.action;
      const insertAt = $from.before(1);
      const tr = state.tr.insert(insertAt, actionType.createAndFill());
      // Don't call tr.scrollIntoView() — that would tug the viewport
      // on every keystroke (especially noticeable at high zoom). The
      // debounced caret-scroll in app.js keeps the caret visible once
      // the typing burst settles.
      dispatch(tr);
    }
    return true;
  }

  // Case 1: at end (or in an empty block) → insert onEnter type after.
  const nextStorage =
    (ELEMENT_TRANSITIONS[curType] && ELEMENT_TRANSITIONS[curType].onEnter) || null;
  if (!nextStorage) return false;
  if (!atEnd) return false;

  const nextNodeType = nodeTypeFor(nextStorage);
  if (!nextNodeType) return false;

  if (dispatch) {
    const insertAt = $from.after(1);
    const tr = state.tr.insert(insertAt, nextNodeType.createAndFill());
    tr.setSelection(TextSelection.near(tr.doc.resolve(insertAt + 1)));
    dispatch(tr);
  }
  return true;
}

// Shift-Enter inserts a soft line break (hard_break inline node)
// without leaving the current block — analogous to Shift-Enter in
// FDD/Highland. Plain Enter is still the block-transition path.
export function insertHardBreak(state, dispatch) {
  if (!schema.nodes.hard_break) return false;
  if (dispatch) {
    const br = schema.nodes.hard_break.create();
    dispatch(state.tr.replaceSelectionWith(br).scrollIntoView());
  }
  return true;
}

// Convenience: build the keymap entries the renderer's mount.js attaches.
// Returns a plain object suitable for `keymap(...)` so the renderer can
// merge it with its own keys (history undo/redo) before building the view.
export function buildEditorKeymap() {
  const map = {
    Tab: cycleType(true),
    'Shift-Tab': cycleType(false),
    Enter: elementTransitionOnEnter,
    'Shift-Enter': insertHardBreak,
    'Mod-b': toggleMark(schema.marks.strong),
    'Mod-i': toggleMark(schema.marks.em),
    'Mod-u': toggleMark(schema.marks.underline),
    'Mod-k': openPaletteCommand,
  };
  for (const [num, storageType] of Object.entries(ELEMENT_QUICK_ASSIGN)) {
    map[`Mod-${num}`] = assignType(storageType);
  }
  return map;
}

// Exposed for unit tests / debugging.
export const __INTERNAL__ = {
  ELEMENT_TRANSITIONS,
  STORAGE_TO_NODE,
  NODE_TO_STORAGE,
  currentStorageType,
  reverseOnTab,
};
